import React, { useEffect, useState } from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    Flex,
    Highlight,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { theme } from '../../../global/style/theme.tsx';
import { t } from 'i18next';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useNavigate } from 'react-router-dom';
import { IconArrowRight, IconDownload, IconMailForward, IconSearch } from '@tabler/icons-react';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import { useElementSize, useMediaQuery } from '@mantine/hooks';
import { useGetClinicPatients } from '../../../global/entities/Patients/queries/useGetClinicPatients.tsx';
import { useDownloadHcfOperationalReport } from '../../../global/entities/HealthcareFacilities/mutations/useDownloadHcfOperationalReport.tsx';
import { patientStore } from '../../../global/Store/Store.ts';
import Utils from '../../../global/helpers/utils.ts';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../global/queryDefaults/QueryKeys.ts';
import { containsStringIgnoreCase } from './PatientTable.tsx';
import { IPatient } from '../../../global/entities/Patients/Patient.ts';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { useResendPatientInvitation } from '../../../global/entities/PatientInvitations/mutations/useResendPatientInvitation.tsx';

const reminderMessageKeys = {
    invitation: {
        action: 'page.patient.resendInvitation',
        confirm: 'page.patient.resendInvitationConfirm',
        success: 'page.patient.invitationResentMessage',
        failure: 'page.patient.invitationResendFailedMessage',
    },
    registration: {
        action: 'page.patient.sendRegistrationReminder',
        confirm: 'page.patient.sendRegistrationReminderConfirm',
        success: 'page.patient.registrationReminderSentMessage',
        failure: 'page.patient.registrationReminderSendFailedMessage',
    },
};

const reminderKeysFor = (patient: IPatient) =>
    patient.isDeletable ? reminderMessageKeys.invitation : reminderMessageKeys.registration;

const ClinicPatientTable: React.FC = inject()(
    observer(() => {
        const navigate = useNavigate();
        const { data, isLoading } = useGetClinicPatients();
        const [search, setSearch] = useState('');
        const { ref, width } = useElementSize();
        const { sortedData, setSortedData, sortBy, setSorting, reverseSortDirection } = useTable();
        const queryClient = useQueryClient();
        const { currentUser } = useSessionProvider();
        const downloadOperationalReport = useDownloadHcfOperationalReport();
        const resendInvitation = useResendPatientInvitation();
        const selectedHealthcareFacilityID = currentUser?.selectedHealthcareFacilityID;

        useEffect(() => {
            if (data?.length)
                setSortedData(data);
        }, [data, setSortedData]);

        const onClickPatient = async (id: string, name: string, birthDate: Date | null, therapeuticIndicationId: string, sexId: string) => {
            try {
                queryClient.invalidateQueries({ queryKey: [QueryKeys.tasks, patientStore.patientId] });

                await Promise.all([
                    patientStore.setPatientId(id),
                    patientStore.setPatientName(name),
                    patientStore.setPatientBirthDate(birthDate?.toString() || ''),
                    patientStore.setTherapeuticIndicationId(therapeuticIndicationId),
                    patientStore.setSexId(sexId),
                ]);

                navigate('/hcpPatientDashboard');
            } catch (error) {
                console.error('Failed to set patient data:', error);
                notifications.show({
                    message: t('notifications.errorLoadingPatient'),
                    color: 'red',
                });
            }
        };

        const handleSendReminder = (patient: IPatient) => {
            const keys = reminderKeysFor(patient);
            modals.openConfirmModal({
                title: t(keys.action),
                children: <p>{t(keys.confirm, { name: patient.name, email: patient.email })}</p>,
                labels: { confirm: t('button.confirm'), cancel: t('button.cancel') },
                onConfirm: () =>
                    resendInvitation.mutate(patient.email, {
                        onSuccess: () => notifications.show({ message: t(keys.success), color: 'green' }),
                        onError: () => notifications.show({ message: t(keys.failure), color: 'red' }),
                    }),
            });
        };

        const openPatient = (patient: IPatient) =>
            onClickPatient(
                patient.id.toString(),
                patient.name,
                patient.birthDate,
                patient.therapeuticIndicationId.toString(),
                patient.sexID.toString()
            );

        const reminderButton = (patient: IPatient) => {
            const label = t(reminderKeysFor(patient).action);
            return (
                <Tooltip label={label}>
                    <ActionIcon
                        variant="transparent"
                        color="blue"
                        aria-label={label}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSendReminder(patient);
                        }}
                    >
                        <IconMailForward size={18} />
                    </ActionIcon>
                </Tooltip>
            );
        };

        const isSmallScreen = useMediaQuery('(max-width: 767px)');
        const isPortrait = useMediaQuery('(orientation: portrait)');
        const isTouchDevice = useMediaQuery('(pointer: coarse)');
        const isMobilePortrait = isSmallScreen && isPortrait && isTouchDevice;

        if (isLoading)
            return <Loading visible={true} loadingMessage={'Loading...'} />;

        const matchesSearch = (patient: IPatient) =>
            containsStringIgnoreCase(
                [`${patient.name}`, patient.primaryHcpName || '', patient.fileNumber || ''],
                search,
            ) || !search;

        const filteredPatients = sortedData?.filter(matchesSearch);

        return (
            <Paper w="100%" ref={ref}>
                <Stack gap={'md'} h={'100%'}>
                    <Flex gap={'sm'} align={'center'}>
                        <TextInput
                            hidden={isMobilePortrait}
                            placeholder={t('global.search')}
                            rightSection={isMobilePortrait ? null : <IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            style={{ flex: 1 }}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        {selectedHealthcareFacilityID && (
                            <Button
                                variant="light"
                                leftSection={<IconDownload size={16} />}
                                loading={downloadOperationalReport.isPending}
                                onClick={() =>
                                    downloadOperationalReport.mutate({
                                        healthcareFacilityId: selectedHealthcareFacilityID,
                                    })
                                }
                            >
                                {t('hcfOperationalReport.downloadButton')}
                            </Button>
                        )}
                    </Flex>

                    {isMobilePortrait ? (
                        <Stack gap="xs">
                            {filteredPatients?.map((patient) => (
                                <Paper
                                    key={patient.id}
                                    withBorder
                                    p="sm"
                                    radius="md"
                                    style={{ cursor: patient.isAccessible ? 'pointer' : 'default' }}
                                    onClick={patient.isAccessible ? () => openPatient(patient) : undefined}
                                >
                                    <Flex justify="space-between" align="flex-start" gap="xs">
                                        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                                            <Text
                                                size="sm"
                                                fw={600}
                                                c={patient.isAccessible ? 'var(--mantine-color-blue-light-color)' : undefined}
                                                style={{ wordBreak: 'break-word' }}
                                            >
                                                {patient.fileNumber
                                                    ? `${patient.name} (${patient.fileNumber})`
                                                    : patient.name}
                                            </Text>
                                            <Flex gap="xs" align="center" wrap="wrap">
                                                <Badge size="xs" variant="light" color="gray">
                                                    {patient.status}
                                                </Badge>
                                                {patient.primaryHcpName && (
                                                    <Text size="xs" c="dimmed">
                                                        {patient.primaryHcpName}
                                                    </Text>
                                                )}
                                                {patient.birthDate && (
                                                    <Text size="xs" c="dimmed">
                                                        {Utils.formatDateToYYYYMMDD(patient.birthDate)}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Stack>
                                        {patient.isAccessible ? (
                                            <ActionIcon variant="transparent" onClick={(e) => e.stopPropagation()}>
                                                <IconArrowRight color={theme.colors?.blue?.toString()} />
                                            </ActionIcon>
                                        ) : (
                                            reminderButton(patient)
                                        )}
                                    </Flex>
                                </Paper>
                            ))}
                        </Stack>
                    ) : (
                    <ScrollArea w="100%">
                        <Table verticalSpacing="xs" mt={'xs'} style={{ tableLayout: 'auto' }}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Th
                                        sorted={sortBy === 'name'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('name', sortedData)}
                                    >
                                        {t('global.patientName')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'fileNumber'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('fileNumber', sortedData)}
                                        hidden={width <= 450}
                                    >
                                        {t('formGlobal.fileNumber')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'primaryHcpName'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('primaryHcpName', sortedData)}
                                    >
                                        {t('page.patient.primaryHcp')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'birthDate'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('birthDate', sortedData)}
                                    >
                                        {t('formProfile.birthDate')}
                                    </Th>
                                    <Th
                                        hidden={width <= 300}
                                        sorted={sortBy === 'status'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('status', sortedData)}
                                    >
                                        {t('table.patient.profileStatus')}
                                    </Th>
                                    <Table.Th hidden={width <= 450}>
                                        {t('global.email')}
                                    </Table.Th>
                                    <Table.Th style={{ width: 'min-content' }} />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {sortedData?.map((patient, index: number) => {
                                    const isSearch = matchesSearch(patient);
                                    return (
                                        <Table.Tr
                                            hidden={!isSearch}
                                            key={index}
                                            style={{
                                                background:
                                                    index % 2 === 1
                                                        ? undefined
                                                        : 'var(--Background-Gray-light, #E9ECEF)',
                                            }}
                                        >
                                            <Table.Td
                                                style={{
                                                    maxWidth: 120,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    cursor: patient.isAccessible ? 'pointer' : 'default',
                                                }}
                                                onClick={patient.isAccessible ? () => openPatient(patient) : undefined}
                                            >
                                                <Highlight
                                                    highlight={search}
                                                    highlightStyles={{
                                                        color: 'var(--mantine-color-gray-9)',
                                                        fontWeight: 700,
                                                        WebkitBackgroundClip: 'text',
                                                    }}
                                                    styles={{
                                                        root: {
                                                            fontSize: 14,
                                                            fontWeight: patient.isAccessible ? 550 : 500,
                                                            whiteSpace: 'nowrap',
                                                            textOverflow: 'ellipsis',
                                                            overflow: 'hidden',
                                                            color: patient.isAccessible
                                                                ? 'var(--mantine-color-blue-light-color)'
                                                                : undefined,
                                                        },
                                                    }}
                                                    truncate
                                                >
                                                    {patient.name}
                                                </Highlight>
                                            </Table.Td>
                                            <Table.Td
                                                hidden={width <= 450}
                                                style={{
                                                    maxWidth: 120,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {patient.fileNumber || '-'}
                                            </Table.Td>
                                            <Table.Td
                                                style={{
                                                    maxWidth: 150,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {patient.primaryHcpName || '-'}
                                            </Table.Td>
                                            <Table.Td>
                                                {patient.birthDate
                                                    ? Utils.formatDateToYYYYMMDD(patient.birthDate)
                                                    : '-'}
                                            </Table.Td>
                                            <Table.Td hidden={width <= 300}>
                                                {patient.status}
                                            </Table.Td>
                                            <Table.Td
                                                hidden={width <= 450}
                                                style={{
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {patient.email}
                                            </Table.Td>
                                            <Table.Td style={{ width: 'min-content' }}>
                                                <Flex justify="flex-end" style={{ whiteSpace: 'nowrap' }}>
                                                    {patient.isAccessible ? (
                                                        <Tooltip label={t('global.patientName')}>
                                                            <ActionIcon
                                                                variant={'transparent'}
                                                                onClick={() => openPatient(patient)}
                                                            >
                                                                <IconArrowRight color={theme.colors?.blue?.toString()} />
                                                            </ActionIcon>
                                                        </Tooltip>
                                                    ) : (
                                                        reminderButton(patient)
                                                    )}
                                                </Flex>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                    )}
                </Stack>
            </Paper>
        );
    }),
);

const WrappedClinicPatientTable: React.FC = () => {
    return (
        <TableContextProvider>
            <ClinicPatientTable />
        </TableContextProvider>
    );
};

export default WrappedClinicPatientTable;
